import React, { useMemo } from 'react';
import { Button, Modal } from 'antd';
import ResultTable from '../../components/table/ResultTable';
import ResultHeader from '../../components/table/ResultHeader';
import ResultBody from '../../components/table/ResultBody';
import ResultRow from '../../components/table/ResultRow';
import ResultHeaderCell from '../../components/table/ResultHeaderCell';
import ResultBodyCell from '../../components/table/ResultBodyCell';
import HecRasHelper from '../../helpers/HecRasHelper';
import {useAppContext} from '../../context/AppContext';

const COLUMNS = [
    { key: 'no', label: 'No', align: 'center' },
    { key: 'x', label: 'X', align: 'right' },
    { key: 'y', label: 'Y', align: 'right' },
    { key: 'river', label: 'River Name', align: 'left' },
    { key: 'length', label: 'Reach Length', align: 'right' },
    { key: 'crossSections', label: '# of XS', align: 'right' },
    { key: 'projectTitle', label: 'Project Title', align: 'left' },
    { key: 'planTitle', label: 'Plan Title', align: 'left' },
    { key: 'folderName', label: 'Folder Name', align: 'left' },
    { key: 'version', label: 'HEC-RAS Version', align: 'left' },
    { key: 'score', label: 'Score', align: 'center' },
];

const buildRow = (history, index, resolveHistorySelection) => {
    const river = history.selection?.base?.river?.river
        ? `${history.selection.base.river.river} - ${history.selection.base.river.reach}`
        : '';
    const resolvedBase = resolveHistorySelection
        ? (resolveHistorySelection(history)?.base || {})
        : (history.selection?.base || {});

    return {
        no: index + 1,
        x: history.summary?.base?.spatial_xy?.[0] || '',
        y: history.summary?.base?.spatial_xy?.[1] || '',
        river,
        length: history.summary?.base?.length || '',
        crossSections: history.summary?.base?.crossSections || '',
        projectTitle: resolvedBase?.project?.information?.title || '',
        planTitle: resolvedBase?.plan?.plan?.title || '',
        folderName: resolvedBase?.project?.information?.folderName || '',
        version: resolvedBase?.plan?.plan?.version || '',
        score: HecRasHelper.calculateOverallScore(history),
    };
};

const formatCellForExport = (row, col) => {
    const value = row[col.key];
    if (col.key === 'score') return value ?? '-';
    return value ?? '';
};

const exportToExcel = (rows) => {
    const header = '<tr>' + COLUMNS.map(c => `<th>${c.label}</th>`).join('') + '</tr>';
    const body = rows.map(row =>
        '<tr>' + COLUMNS.map(c => `<td>${formatCellForExport(row, c)}</td>`).join('') + '</tr>'
    ).join('');

    const html =
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">' +
        '<head><meta charset="UTF-8">' +
        '<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>' +
        '<x:Name>Summary</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>' +
        '</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->' +
        '</head><body><table border="1">' + header + body + '</table></body></html>';

    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch_run_summary_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export default function SummaryTableModal({ visible, onClose, histories }) {
    const {resolveHistorySelection} = useAppContext()
    const rows = useMemo(() => {
        return (histories || [])
            .filter(h => h.type === 'Check')
            .map((h, idx) => buildRow(h, idx, resolveHistorySelection));
    }, [histories, resolveHistorySelection]);

    const hasRows = rows.length > 0;

    return (
        <Modal
            title="Batch Run Inventory Summary Table"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={'90vw'}
        >
            <div style={{ marginBottom: 8 }}>
                <Button
                    size={'small'}
                    disabled={!hasRows}
                    onClick={() => exportToExcel(rows)}
                >
                    Export Excel
                </Button>
            </div>

            {!hasRows ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                    No batch run results available. Please run some projects first.
                </div>
            ) : (
                <ResultTable scrollable={{ height: 400 }} style={{ width: '100%' }} maxWidth={'100%'}>
                    <ResultHeader>
                        <ResultRow>
                            {COLUMNS.map(col => (
                                <ResultHeaderCell key={col.key} style={{ textAlign: col.align }}>
                                    {col.label}
                                </ResultHeaderCell>
                            ))}
                        </ResultRow>
                    </ResultHeader>
                    <ResultBody>
                        {rows.map(row => (
                            <ResultRow key={`${row.no}-${row.folderName}-${row.planTitle}`}>
                                {COLUMNS.map(col => {
                                    const value = row[col.key];
                                    const baseStyle = { textAlign: col.align };
                                    if (col.key === 'score') {
                                        return (
                                            <ResultBodyCell
                                                key={col.key}
                                                style={{
                                                    ...baseStyle,
                                                    color: HecRasHelper.getScoreColor(value),
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {value !== null && value !== undefined ? value : '-'}
                                            </ResultBodyCell>
                                        );
                                    }
                                    return (
                                        <ResultBodyCell key={col.key} style={baseStyle}>
                                            {value !== '' && value !== null && value !== undefined ? value : '-'}
                                        </ResultBodyCell>
                                    );
                                })}
                            </ResultRow>
                        ))}
                    </ResultBody>
                </ResultTable>
            )}
        </Modal>
    );
}
